/* eslint-disable max-len */
import { FC, KeyboardEvent, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import * as prismicH from '@prismicio/helpers';
import { PrismicRichText } from '@prismicio/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Autoplay, FreeMode, Navigation } from 'swiper/modules';
import {
  Container,
  Heading,
  Icon,
  IconSvg,
  Link,
  PrismicImageWithAlt,
} from '@/components';
import { ImageGallerySlice } from '@/types';
import {
  useFilteredSliceItemsByLocationSlug,
  useIsMobile,
  useScreenWidth,
} from '@/hooks';
import { getContentVerticalAlign, htmlSerializerWithProps } from '@/utils';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import style from './image_gallery.module.scss';

export type TextAlign = 'center' | 'left' | 'right';

/** Variations that expose `content_align` in Prismic. */
const CONTENT_ALIGN_VARIATIONS = [
  'default',
  'imageGridWithTextOver',
  'imageHorizontalDimensions',
  'imageGridWithoutTabs',
  'imageWithSquareDimensions',
  'imageGridWithCustomWidth',
] as const;

export const hasContentAlignField = (variation: string): boolean =>
  (CONTENT_ALIGN_VARIATIONS as readonly string[]).includes(variation);

/** Prismic content_align for class/textAlign use; null if the variation has no field. */
export const getGalleryContentAlign = (
  variation: string,
  contentAlign?: string | null,
): TextAlign | null => {
  if (!hasContentAlignField(variation)) {
    return null;
  }

  if (
    contentAlign === 'left' ||
    contentAlign === 'right' ||
    contentAlign === 'center'
  ) {
    return contentAlign;
  }

  return 'center';
};

const GalleryVariation: FC<{
  slice: ImageGallerySlice;
}> = ({ slice }) => {
  const { variation, items, primary } = slice;
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(items);
  const { isMobile } = useIsMobile();
  const { width } = useScreenWidth();
  const [hovered, setHovered] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('tab 1');
  const tabButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const imageMaxWidth =
    variation === 'imageGridWithCustomWidth' ? primary?.image_max_width : 0;

  const contentAlign = getGalleryContentAlign(
    variation,
    (primary as { content_align?: string })?.content_align,
  );

  const orderedTabs = useMemo(() => {
    if (variation !== 'imageGridWithTextOver') {
      return [] as Array<{
        id: string;
        title: string;
        items: typeof filteredItems;
      }>;
    }

    const orderList: Array<{
      id: string;
      title: string;
      items: typeof filteredItems;
    }> = [];

    Array.from({ length: 4 }).forEach((_, index) => {
      const id = `tab ${index + 1}`;
      const title = primary[`title_tab_${index + 1}`] ?? `TAB ${index + 1}`;
      const content = filteredItems?.filter(
        i => i.grouped_in === id || (!i.grouped_in && index === 0),
      );

      if (
        content.length > 0 ||
        primary[`title_tab_${index + 1}`] ||
        index === 0
      ) {
        orderList.push({
          id,
          title,
          items: content,
        });
      }
    });

    return orderList;
  }, [variation, filteredItems, primary]);

  // Shared across variations; item shapes differ per Prismic variation.
  const contentList: any[] =
    variation === 'imageGridWithTextOver'
      ? orderedTabs.find(tab => tab.id === activeTab)?.items ?? []
      : items;

  const getGalleryItemLabel = (item: {
    title_caption?: unknown;
    heading_caption?: unknown;
    cta_text_caption?: string | null;
  }) =>
    prismicH.asText(item?.title_caption as any)?.trim() ||
    prismicH.asText(item?.heading_caption as any)?.trim() ||
    item?.cta_text_caption?.trim() ||
    'gallery image';

  const renderImage = image => {
    if (!image?.url) {
      return <IconSvg name="image" />;
    }

    return (
      <PrismicImageWithAlt
        field={image}
        fill
        style={{ objectFit: 'cover', objectPosition: 'center center' }}
      />
    );
  };

  const getSpacingBetween = () => {
    if (width < 768) {
      return 12;
    }

    if (width < 1024) {
      return 70;
    }

    if (width < 1280) {
      return 100;
    }

    return 130;
  };

  const renderContent = (item, index) => {
    if (variation === 'imageGridWithCustomWidth') {
      const { gallery_image } = item;

      const getAspectRatio = () => {
        const aspectRatio =
          gallery_image?.dimensions?.height / gallery_image?.dimensions?.width;

        return `${aspectRatio * 100}%`;
      };

      return (
        <figure
          className={classNames(
            style.gallery__images_item,
            style.gallery__images_item_custom_width,
          )}
          style={{ paddingTop: getAspectRatio() }}
        >
          {gallery_image?.url ? (
            <PrismicImageWithAlt
              field={gallery_image}
              fill
              style={{
                objectFit: 'cover',
                objectPosition: 'center center',
              }}
            />
          ) : (
            <IconSvg name="image" />
          )}
        </figure>
      );
    }
    if (
      variation === 'default' ||
      variation === 'imageGridWithTextOver' ||
      variation === 'imageWithSquareDimensions' ||
      variation === 'imageGridWithoutTabs'
    ) {
      const {
        title_caption_color,
        heading_caption_color,
        description_caption_color,
        cta_type_caption,
        cta_background_color_caption,
        cta_background_color_hover_caption,
        cta_text_color_caption,
        cta_text_color_hover_caption,
        cta_border_caption,
        cta_border_hover_caption,
        background_color_caption,
        border_caption,
        disable_caption,
      } = primary;

      const {
        gallery_image,
        title_caption,
        heading_caption,
        description_caption,
        cta_text_caption,
        cta_link_caption,
      } = item;

      const hasCaption =
        prismicH.asText(title_caption) ||
        prismicH.asText(heading_caption) ||
        prismicH.asText(description_caption) ||
        cta_text_caption;

      const applyShadowEffect =
        (variation === 'imageGridWithTextOver' ||
          variation === 'imageGridWithoutTabs') &&
        primary?.apply_shadow_effect;

      return (
        <>
          {hasCaption && (
            <div
              className={classNames(style.caption__container, {
                [style.caption__container_disable]: disable_caption,
                [style.caption__container_tabs]:
                  variation === 'imageGridWithTextOver' ||
                  variation === 'imageGridWithoutTabs',
                [style.caption__container_tabs_gradient_effect]:
                  applyShadowEffect,
                [style[`caption__container_align_${contentAlign}`]]:
                  !!contentAlign,
              })}
              style={{
                background: (!disable_caption
                  ? background_color_caption
                  : '') as string,
                border: (!disable_caption ? border_caption : '') as string,
              }}
            >
              {renderTextSlice(
                heading_caption,
                heading_caption_color ?? '',
                '100%',
                contentAlign ?? 'center',
              )}
              {renderTextSlice(
                title_caption,
                title_caption_color ?? '',
                '100%',
                contentAlign ?? 'center',
              )}
              {renderTextSlice(
                description_caption,
                description_caption_color ?? '',
                '100%',
                contentAlign ?? 'center',
              )}
              {cta_text_caption && (
                <Link
                  aria-label={`Go to ${cta_text_caption}`}
                  className={classNames(style.caption__cta_button, {
                    [style.caption__cta_button_primary]:
                      cta_type_caption === 'button',
                    [style.caption__cta_button_link]:
                      cta_type_caption === 'link',
                  })}
                  href={cta_link_caption ?? '/'}
                  onMouseEnter={() => {
                    setHovered(index);
                  }}
                  onMouseLeave={() => {
                    setHovered(null);
                  }}
                  style={{
                    backgroundColor: (hovered === index
                      ? cta_background_color_hover_caption
                      : cta_background_color_caption) as string,
                    color: (hovered === index
                      ? cta_text_color_hover_caption
                      : cta_text_color_caption) as string,
                    border: (hovered === index
                      ? cta_border_hover_caption
                      : cta_border_caption) as string,
                  }}
                >
                  {cta_text_caption}
                </Link>
              )}
            </div>
          )}
          <figure
            className={classNames(style.gallery__images_item, {
              [style.gallery__images_item_square]:
                variation === 'imageWithSquareDimensions',
            })}
          >
            {renderImage(gallery_image)}
          </figure>
        </>
      );
    }

    return;
  };

  const selectGalleryTab = (tabId: string, index: number) => {
    setActiveTab(tabId);
    tabButtonRefs.current[index]?.focus();
  };

  const handleGalleryTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const tabs = orderedTabs;
    if (!tabs.length) {
      return;
    }

    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    selectGalleryTab(tabs[nextIndex].id, nextIndex);
  };

  const renderCarousel = (
    auto: boolean,
    centerSlice: boolean,
    classCarousel: string,
    classSwiper: string,
    spaceBetween: any,
  ) => {
    if (contentList.length === 0) {
      return null;
    }

    return (
      <Swiper
        a11y={{
          enabled: true,
          nextSlideMessage: 'Next slide',
          prevSlideMessage: 'Previous slide',
        }}
        autoplay={
          auto && {
            delay: 5000,
            disableOnInteraction: false,
          }
        }
        centeredSlides={
          variation === 'imageGridWithTextOver' ||
          variation === 'imageGridWithoutTabs'
            ? false
            : centerSlice
        }
        className={classCarousel}
        effect={'fade'}
        freeMode
        loop={
          variation !== 'imageGridWithTextOver' &&
          variation !== 'imageGridWithoutTabs'
        }
        modules={[A11y, Autoplay, Navigation, FreeMode]}
        navigation
        slidesPerView={'auto'}
        spaceBetween={spaceBetween ? spaceBetween : getSpacingBetween()}
      >
        {contentList?.map((item, index) => {
          const imageLink =
            variation === 'imageGridWithoutTabs' &&
            !item?.cta_text_caption &&
            item?.cta_link_caption
              ? item?.cta_link_caption
              : null;

          return (
            <SwiperSlide className={classSwiper} key={index}>
              {variation === 'imageCorousel' && (
                <figure className={style.gallery_carousel__slide}>
                  {renderImage(item?.gallery_image)}
                </figure>
              )}
              {(variation === 'imageGridWithTextOver' ||
                variation === 'imageGridWithoutTabs') &&
                (imageLink ? (
                  <Link
                    aria-label={`Go to ${getGalleryItemLabel(item)}`}
                    href={imageLink}
                  >
                    {renderContent(item, index)}
                  </Link>
                ) : (
                  renderContent(item, index)
                ))}
            </SwiperSlide>
          );
        })}
      </Swiper>
    );
  };

  if (variation === 'imageGrid') {
    return (
      <div
        className={classNames(
          style.gallery_grid,
          style[`grid_${items.length}`],
        )}
      >
        {items.slice(0, 6).map((item, index) => (
          <figure
            className={classNames(
              style.gallery_grid__item,
              style[`grid_item__${index + 1}`],
            )}
            key={index}
          >
            {renderImage(item?.gallery_image)}
          </figure>
        ))}
      </div>
    );
  }

  if (
    variation === 'default' ||
    variation === 'imageGridWithTextOver' ||
    variation === 'imageWithSquareDimensions' ||
    variation === 'imageGridWithoutTabs' ||
    variation === 'imageGridWithCustomWidth'
  ) {
    // vertical_align only applies to imageGridWithCustomWidth (desktop)
    const itemsVerticalAlign =
      variation === 'imageGridWithCustomWidth' &&
      !isMobile &&
      primary?.vertical_align;

    const tabsAlign = contentAlign ?? 'center';

    return (
      <>
        {variation === 'imageGridWithTextOver' && (
          <Container className={style.gallery__images_tabs_container}>
            <ul
              className={classNames(style.gallery__images_tabs, {
                [style.gallery__images_tabs_align_left]: tabsAlign === 'left',
                [style.gallery__images_tabs_align_center]:
                  tabsAlign === 'center',
                [style.gallery__images_tabs_align_right]: tabsAlign === 'right',
                // Legacy flag only when content_align is center
                [style.gallery__images_tabs_centered]:
                  tabsAlign === 'center' && !!primary?.centered_tabs,
              })}
              role="tablist"
            >
              {orderedTabs?.map((tab, index) => {
                const isSelected = activeTab === tab.id;
                const tabId = `gallery-tab-${index}`;

                return (
                  <li key={`tab_${index}`} role="presentation">
                    <button
                      aria-controls="gallery-tabpanel"
                      aria-selected={isSelected}
                      className={classNames(style.gallery__images_tabs_tab, {
                        [style.gallery__images_tabs_tab_active]: isSelected,
                      })}
                      id={tabId}
                      onClick={() => selectGalleryTab(tab.id, index)}
                      onKeyDown={event => handleGalleryTabKeyDown(event, index)}
                      ref={element => {
                        tabButtonRefs.current[index] = element;
                      }}
                      role="tab"
                      tabIndex={isSelected ? 0 : -1}
                      type="button"
                    >
                      {tab.title}{' '}
                      <span
                        style={{ color: primary?.tab_counter_color ?? '' }}
                      >{`(${tab.items?.length})`}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Container>
        )}
        {contentList?.length > 0 ? (
          <>
            {(variation === 'imageGridWithTextOver' ||
              variation === 'imageGridWithoutTabs') &&
              renderCarousel(
                false,
                true,
                style.gallery__images_carousel,
                style.gallery__images_item_body,
                24,
              )}
            <div
              className={classNames(style.gallery__images, {
                [style.gallery__images_hidden]:
                  variation === 'imageGridWithTextOver' ||
                  variation === 'imageGridWithoutTabs',
                [style.gallery__images_custom_width]:
                  variation === 'imageGridWithCustomWidth',
                [style[`gallery__images_align_${contentAlign}`]]:
                  !!contentAlign,
              })}
              {...(variation === 'imageGridWithTextOver'
                ? {
                    'aria-labelledby': `gallery-tab-${Math.max(
                      0,
                      orderedTabs.findIndex(tab => tab.id === activeTab),
                    )}`,
                    id: 'gallery-tabpanel',
                    role: 'tabpanel',
                  }
                : {})}
              style={
                itemsVerticalAlign
                  ? {
                      alignItems: getContentVerticalAlign(
                        itemsVerticalAlign as 'top' | 'center' | 'bottom',
                      ),
                    }
                  : undefined
              }
            >
              {contentList?.map((item, index) => {
                const imageLink =
                  variation === 'imageGridWithoutTabs' &&
                  !item?.cta_text_caption &&
                  item?.cta_link_caption
                    ? item?.cta_link_caption
                    : null;

                return (
                  <div
                    className={classNames(style.gallery__images_item_body, {
                      [style.gallery__images_item_body_square]:
                        variation === 'imageWithSquareDimensions',
                      [style.gallery__images_item_body_custom_width]:
                        variation === 'imageGridWithCustomWidth',
                    })}
                    key={index}
                    style={{
                      maxWidth:
                        variation === 'imageGridWithCustomWidth'
                          ? (imageMaxWidth as number)
                          : '',
                    }}
                  >
                    {imageLink ? (
                      <Link
                        aria-label={`Go to ${getGalleryItemLabel(item)}`}
                        href={imageLink}
                      >
                        {renderContent(item, index)}
                      </Link>
                    ) : (
                      renderContent(item, index)
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className={style.gallery__images_empty_results}>
            <Icon name="search" />
            <Heading level={5}>There are no images to show</Heading>
            <p>{'No images have been entered in the selected category'}</p>
          </div>
        )}
      </>
    );
  }

  if (variation === 'imageHorizontalDimensions') {
    const { item_heading_color, item_title_color, item_description_color } =
      primary;

    return (
      <div
        className={classNames(style.gallery__images, {
          [style.horizontal_image]: variation === 'imageHorizontalDimensions',
          [style[`gallery__images_align_${contentAlign}`]]: !!contentAlign,
        })}
      >
        {contentList?.map((item, index) => {
          const hasCaption =
            prismicH.asText(item?.item_heading) ||
            prismicH.asText(item?.item_title) ||
            prismicH.asText(item?.item_description);
          const itemLabel =
            prismicH.asText(item?.item_title)?.trim() ||
            prismicH.asText(item?.item_heading)?.trim() ||
            'gallery image';
          const href = (item?.link as string) || '/';

          return (
            <div
              className={classNames(style.gallery__images_item_body, {
                [style.gallery__images_item_body_horizontal_image]:
                  variation === 'imageHorizontalDimensions',
              })}
              key={index}
            >
              <Link
                aria-label={`Go to ${itemLabel}`}
                className={style.gallery__horizontal_link}
                href={href}
              >
                {hasCaption && (
                  <div
                    className={classNames(
                      style.caption__container,
                      style.caption__container_disable,
                      {
                        [style[`caption__container_align_${contentAlign}`]]:
                          !!contentAlign,
                      },
                    )}
                  >
                    {renderTextSlice(
                      item?.item_heading,
                      item_heading_color ?? '',
                      '100%',
                      contentAlign ?? 'center',
                    )}
                    {renderTextSlice(
                      item?.item_title,
                      item_title_color ?? '',
                      '100%',
                      contentAlign ?? 'center',
                    )}
                    {renderTextSlice(
                      item?.item_description,
                      item_description_color ?? '',
                      '100%',
                      contentAlign ?? 'center',
                    )}
                  </div>
                )}
                <figure
                  className={classNames(style.gallery__images_item, {
                    [style.gallery__images_item_horizontal_image]:
                      variation === 'imageHorizontalDimensions',
                  })}
                >
                  {renderImage(item?.gallery_image)}
                </figure>
              </Link>
            </div>
          );
        })}
      </div>
    );
  }

  return renderCarousel(
    true,
    true,
    style.gallery_carousel,
    style.gallery__item_slice,
    null,
  );
};

const CTAButton: FC<ImageGallerySlice> = ({ primary }) => {
  const [hovered, setHovered] = useState(false);
  const {
    cta_text,
    cta_type,
    cta_text_color,
    cta_link,
    cta_background_color,
    cta_background_color_hover,
    cta_text_color_hover,
    cta_border,
    cta_border_hover,
  } = primary;

  if (!cta_text) {
    return null;
  }

  return (
    <div className={style.gallery_cta}>
      <Link
        aria-label={`Go to ${cta_text}`}
        className={classNames(style.cta_button, {
          [style.cta_button_primary]: cta_type === 'button',
        })}
        href={cta_link ?? ''}
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
        style={{
          backgroundColor: (hovered
            ? cta_background_color_hover
            : cta_background_color) as string,
          color: (hovered ? cta_text_color_hover : cta_text_color) as string,
          border: (hovered ? cta_border_hover : cta_border) as string,
        }}
      >
        {cta_text}
      </Link>
    </div>
  );
};

const renderTextSlice = (
  text: any,
  color: string,
  maxWidth?: string,
  contentAlign?: TextAlign,
) => {
  if (!prismicH.asText(text)) {
    return null;
  }

  const textAlign = contentAlign ?? 'center';

  return (
    <PrismicRichText
      components={htmlSerializerWithProps({
        color: color,
        maxWidth: maxWidth,
        textAlign: textAlign,
      })}
      field={text}
    />
  );
};

export { CTAButton, GalleryVariation, renderTextSlice };
